import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { Modal } from 'semantic-ui-react';
import PropTypes from 'prop-types';

import PDFViewer from '../../../responses/components/file-manager/components/modals/preview/pdf-viewer';
import { getESignAuditTrail, uploadESignDocument } from '../../../form/slice-actions/form';
import { toggleESignModal, updateESign } from '../../../form/reducer/form';
import useWindowDimensions from '../../../../hooks/window-dimensions';
import { ESignPreviewModalWrapper } from '../form-preview.styled';
import { emailTestPatterUnicode } from '../../../../shared/utils';
import { useRenderPDF } from '../../../../hooks/render-pdf';
import Loader from './loader';

const modalStyles = {
  mobile: {
    height: 'calc(100vh - 30px)',
    borderRadius: 0,
    width: '100vw',
    bottom: 0
  },
  desktop: {
    height: '95vh'
  }
};

const ESignPreviewModal = ({ onCompleted, form }) => {
  const { error: eSignError, isLoading, auditTrail } = useSelector((state) => state.form.eSign);
  const {
    totalResponsePages,
    form_owner_email,
    form_owner_name,
    id: documentId,
    audit_trail,
    timezone,
    signed,
    email,
    name
  } = auditTrail;

  const [signeeDataErrors, setSigneeDataErrors] = useState({ name: '', email: '' });
  const [pdfData, updatePDFData] = useState(form);
  const { width } = useWindowDimensions();
  const dispatch = useDispatch();
  
  const { document, loading, totalPages, error: pdfRenderingError, url } = useRenderPDF({
    templateProps: pdfData
  });
  
  const modalStyle = modalStyles[width >= 768 ? 'desktop' : 'mobile'];
  const handleGetAuditTrail = () => dispatch(getESignAuditTrail());
  const closeModal = () => dispatch(toggleESignModal(false));
  const isDocumentReady = document && documentId && !loading;

  const isAuditTrailRendered = isDocumentReady && totalPages - totalResponsePages === 1;
  const disableInputs = isAuditTrailRendered || isLoading || loading || signed;

  const handleESignModelChange = (e) => {
    const { target: { value, name }} = e;
    dispatch(updateESign({
      auditTrail: {
        ...auditTrail,
        [name]: value
      }
    }));
  };

  const isSigneeDataValid= () => {
    const signeeData = [
      { prop: 'email', value: email, error: '' },
      { prop: 'name', value: name, error: '' }
    ];

    signeeData.forEach((data) => {
      const { prop, value } = data;
      const newValue = value.toString().trim();

      if (prop === 'name') {
        const namePattern = /^[A-Za-z]+([ '-][A-Za-z]+)*$/;
        const fullName = newValue.split(/\s+/);

        if (!namePattern.test(newValue)) data.error = 'Full name contains invalid character(s).';
        if (fullName.length < 2) data.error = 'A minimum of 2 names are required.';
        if (fullName.length > 3) data.error = 'A maximum of 3 names are allowed.';
        if (newValue.length < 3 || newValue.length > 100) {
          data.error = 'Full name must be between 3 - 100 characters in length.';
        };
        if (fullName.length > 1) {
          fullName.forEach((name) => {
            if (name.length < 2) data.error = 'First/middle/last name must be greater than 1 character.'
          });
        }

        setSigneeDataErrors((prev) => ({ ...prev, [prop]: data.error }));
      }

      if (prop === 'email') {
        if (!emailTestPatterUnicode.test(newValue)) data.error = 'Provided text is not a valid email.';
        setSigneeDataErrors((prev) => ({ ...prev, [prop]: data.error }));
      }
    });
    
    return !signeeData.some(({ error }) => error);
  };

  const handleClick = () => {
    if (!isSigneeDataValid()) return;
    if (isAuditTrailRendered) return dispatch(uploadESignDocument(document, onCompleted));
    handleGetAuditTrail();
  };
  
  useEffect(() => {
    if (documentId) {
      if (!totalResponsePages) dispatch(updateESign({
        auditTrail: {
          ...auditTrail,
          totalResponsePages: totalPages
        }
      }));

      updatePDFData({
        ...form,
        auditTrail: {
          timeStamp: audit_trail[0].action_timestamp,
          ipAddress: audit_trail[0].ip_address,
          form_owner_email,
          form_owner_name,
          documentId,
          timezone,
          email,
          name
        }
      });
    }
  }, [documentId]);
  
  return (
    <Modal size="small" open style={modalStyle}>
      <ESignPreviewModalWrapper>
        <button type="button" className="close-icon" onClick={closeModal}>
          <img src="https://storage.cloud.google.com/fplsblog/e-sign/close-icon-white.svg" alt="close_icon" />
        </button>

        <div className="header">
          <h1>Preview Signed Document</h1>
        </div>

        <div className="content">
          {(loading || signed) && (<Loader text={signed ? 'Submitting your response...' : ''} />)}
          {url && !signed && <PDFViewer showPaginationOnMobile src={url} />}

          {pdfRenderingError && (
            <div className="error">
              An error occurred while previewing your responses.
            </div>
          )}
        </div>

        <div className="footer">
          <p>
            I understand and accept that my electronic signature will be valid as a handwritten signature and considered original to the extent allowed by applicable law. 
            <span>
              For more information please see the <a href="/consumer-consent-disclosure" target="_blank">Consumer Consent Disclosure.</a>
            </span>
          </p>

          <div className="meta">
            <div>
              <label htmlFor="e-sign-fullname">Full Name</label>
              <input
                onChange={handleESignModelChange}
                placeholder="Enter full name"
                disabled={disableInputs}
                id="e-sign-fullname"
                defaultValue={name}
                name="name"
                type="text"
              />
              {signeeDataErrors.name && (<div className="error">{signeeDataErrors.name}</div>)}
            </div>
            
            <div>
              <label htmlFor="e-sign-email">Email</label>
              <input
                onChange={handleESignModelChange}
                placeholder="Enter E-mail"
                disabled={disableInputs}
                defaultValue={email}
                id ="e-sign-email"
                type="email"
                name="email"
              />
              {signeeDataErrors.email && (<div className="error">{signeeDataErrors.email}</div>)}
            </div>
          </div>

          {eSignError && (<div className="error">{eSignError}</div>)}

          {isAuditTrailRendered && <small>Your consent has been received successfully!</small>}
          <button
            disabled={!name || !email || isLoading || signed}
            className="proceed-button"
            onClick={handleClick}
            type="submit"
          >
            {isLoading ? `Processing...` : isAuditTrailRendered ? 'Complete' : 'Proceed'}
          </button>
        </div>
      </ESignPreviewModalWrapper>
    </Modal>
  );
};

ESignPreviewModal.propTypes = {
  form: PropTypes.objectOf(PropTypes.any).isRequired,
  onCompleted: PropTypes.func.isRequired
};

export default ESignPreviewModal;
